import { supabase } from "~/integrations/supabase/client";
import type {
  CreateTransferResult,
  IngredientTransferWithItems,
  TransferItemInput,
} from "~/types/ingredientTransfer";
import type { Plant } from "~/types/purchasing";
import type { Json } from "~/types/supabase";

// Header + line items + joined ingredient names + performer, newest first.
const TRANSFER_SELECT = `
  *,
  items:ingredient_transfer_items(
    *,
    oil_type:oil_types(id, name, code, density_kg_per_liter),
    raw_material:raw_materials(id, name, code, unit)
  ),
  performed_by_profile:profiles!performed_by(id, name, lastname)
`;

interface CreateTransferParams {
  fromPlant: Plant;
  toPlant: Plant;
  items: TransferItemInput[];
  notes?: string | null;
  performedBy: string;
}

export const ingredientTransferService = {
  /**
   * List all ingredient transfers with their line items (newest first).
   */
  async listTransfers(): Promise<IngredientTransferWithItems[]> {
    const { data, error } = await supabase
      .from("ingredient_transfers")
      .select(TRANSFER_SELECT)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return (data ?? []) as unknown as IngredientTransferWithItems[];
  },

  /**
   * Fetch a single transfer with its line items.
   */
  async getTransferById(id: string): Promise<IngredientTransferWithItems> {
    const { data, error } = await supabase
      .from("ingredient_transfers")
      .select(TRANSFER_SELECT)
      .eq("id", id)
      .single();

    if (error) throw error;
    return data as unknown as IngredientTransferWithItems;
  },

  /**
   * Create a transfer. The RPC atomically decrements the source plant,
   * increments the destination plant, keeps the global cache in sync, and
   * logs the two audit legs per line.
   */
  async createTransfer(
    params: CreateTransferParams
  ): Promise<CreateTransferResult> {
    const { data, error } = await supabase.rpc("create_ingredient_transfer", {
      p_from_plant: params.fromPlant,
      p_to_plant: params.toPlant,
      p_items: params.items as unknown as Json,
      p_notes: params.notes ?? null,
      p_performed_by: params.performedBy,
    });

    if (error) throw error;
    return data as unknown as CreateTransferResult;
  },
};
